import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Input } from './inputs/Input';
import { PrimaryButton, SecondaryButton } from './Button';
import { gql } from '@apollo/client';
import { useOrganizationStore } from '../store/organization';
import { useEventStore } from '../store/event';
import { createApolloClient } from '../providers/ApolloClientFactory';

const GET_ATTENDEES_BY_ORGANIZATION_AND_EVENT = gql`
  query AttendeesByOrganizationAndEvent($organizationId: Int!, $eventId: Int) {
    attendees(where: {
      event: {
        organization_id: { _eq: $organizationId }
        _and: { id: { _eq: $eventId } }
      }
    }) {
      id
      first_name
      last_name
      phone_number
      email
      event {
        id
        name
      }
    }
  }
`;

const GET_ATTENDEES_BY_ORGANIZATION = gql`
  query AttendeesByOrganization($organizationId: Int!) {
    attendees(where: { event: { organization_id: { _eq: $organizationId } } }) {
      id
      first_name
      last_name
      phone_number
      email
      event {
        id
        name
      }
    }
  }
`;

interface Attendee {
  id: number;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  event: {
    id: number;
    name: string;
  };
}

interface AssignAttendeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (attendeeId: number) => void;
}

const client = createApolloClient();

export default function AssignAttendeeModal({
  isOpen,
  onClose,
  onConfirm,
}: AssignAttendeeModalProps) {
  const organizationId = useOrganizationStore((state) => state.organizationId);
  const eventId = useEventStore((state) => state.eventId);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedAttendeeId, setSelectedAttendeeId] = useState<number | null>(null);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAttendees = async () => {
      if (!isOpen || !organizationId) return;

      setLoading(true);
      setError(null);

      try {
        // If an event is selected, filter by both organization and event
        // Otherwise, just filter by organization
        const query = eventId
          ? GET_ATTENDEES_BY_ORGANIZATION_AND_EVENT
          : GET_ATTENDEES_BY_ORGANIZATION;

        const variables = eventId
          ? { organizationId, eventId }
          : { organizationId };

        const { data } = await client.query({
          query,
          variables,
          fetchPolicy: 'network-only',
        });
        setAttendees(data?.attendees || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load attendees');
        console.error('Error fetching attendees:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendees();
  }, [isOpen, organizationId, eventId]);

  // Filter attendees based on search criteria
  const filteredAttendees = attendees.filter((attendee) => {
    const firstNameMatch = firstName
      ? attendee.first_name?.toLowerCase().includes(firstName.toLowerCase())
      : true;
    const lastNameMatch = lastName
      ? attendee.last_name?.toLowerCase().includes(lastName.toLowerCase())
      : true;
    const emailMatch = email
      ? attendee.email?.toLowerCase().includes(email.toLowerCase())
      : true;
    const phoneMatch = phoneNumber
      ? attendee.phone_number?.includes(phoneNumber)
      : true;

    return firstNameMatch && lastNameMatch && emailMatch && phoneMatch;
  });

  const handleConfirm = () => {
    if (selectedAttendeeId) {
      onConfirm(selectedAttendeeId);
      handleClose();
    }
  };

  const handleClose = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhoneNumber('');
    setSelectedAttendeeId(null);
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-6 py-8 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  Assign to Attendee
                </Dialog.Title>

                {/* Search Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <Input
                      name="firstName"
                      placeholder="Search by First Name"
                      type="text"
                      defaultValue={firstName}
                      onChange={(value) => setFirstName(value as string)}
                    />
                  </div>
                  <div>
                    <Input
                      name="lastName"
                      placeholder="Search by Last Name"
                      type="text"
                      defaultValue={lastName}
                      onChange={(value) => setLastName(value as string)}
                    />
                  </div>
                  <div>
                    <Input
                      name="email"
                      placeholder="Search by Email"
                      type="email"
                      defaultValue={email}
                      onChange={(value) => setEmail(value as string)}
                    />
                  </div>
                  <div>
                    <Input
                      name="phoneNumber"
                      placeholder="Search by Phone Number"
                      type="text"
                      defaultValue={phoneNumber}
                      onChange={(value) => setPhoneNumber(value as string)}
                    />
                  </div>
                </div>

                {/* Attendees List */}
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Select Attendee ({filteredAttendees.length} results)
                  </h4>
                  <div className="border border-gray-300 rounded-md max-h-96 overflow-y-auto">
                    {loading && (
                      <div className="p-4 text-center text-gray-500">Loading attendees...</div>
                    )}
                    {error && (
                      <div className="p-4 text-center text-red-500">
                        Error loading attendees: {error}
                      </div>
                    )}
                    {!loading && !error && filteredAttendees.length === 0 && (
                      <div className="p-4 text-center text-gray-500">
                        No attendees found matching your search criteria
                      </div>
                    )}
                    {!loading &&
                      !error &&
                      filteredAttendees.map((attendee) => (
                        <div
                          key={attendee.id}
                          onClick={() => setSelectedAttendeeId(attendee.id)}
                          className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors ${
                            selectedAttendeeId === attendee.id
                              ? 'bg-primary-100 hover:bg-primary-200'
                              : ''
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="font-medium text-gray-900">
                                <strong>Name:</strong> {attendee.first_name} {attendee.last_name}
                              </p>
                              <p className="text-sm text-gray-600"> <strong>Email: </strong>{attendee.email}</p>
                              <p className="text-sm text-gray-600"> <strong>Phone: </strong>{attendee.phone_number}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                <strong>Event: </strong> {attendee.event?.name}
                              </p>
                            </div>
                            <div>
                              {selectedAttendeeId === attendee.id && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-600 text-white">
                                  Selected
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-6 flex justify-end gap-3">
                  <SecondaryButton onClick={handleClose}>Cancel</SecondaryButton>
                  <PrimaryButton onClick={handleConfirm} isDisabled={!selectedAttendeeId}>
                    Assign
                  </PrimaryButton>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}